import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useAuth } from "./AuthContext";
import * as authApi from "../api/auth";
import * as paisesApi from "../api/paises";
import CampoPassword from "../components/CampoPassword";
import { rutaInicio } from "../utils/rutaInicio";

const SEGUNDOS_REDIRECCION = 5;

function validarPassword(password) {
  if (password.length < 8) return "La contraseña debe tener al menos 8 caracteres.";
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) return "La contraseña debe incluir letras y números.";
  return null;
}

export default function Login() {
  const { iniciarSesion } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  const [mostrarRegistro, setMostrarRegistro] = useState(false);
  const [mostrarRecuperar, setMostrarRecuperar] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      const usuarioLogueado = await iniciarSesion(email, password);
      navigate(rutaInicio(usuarioLogueado));
    } catch (err) {
      setError(err.response?.data?.mensaje || "No se pudo iniciar sesión.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-fs-primary-light px-3"
         style={{ background: "linear-gradient(160deg, #0C8599 0%, #075463 100%)" }}>
      <div className="fs-card shadow-lg" style={{ maxWidth: 420, width: "100%" }}>
        <div className="text-center mb-4">
          <div className="fs-display" style={{ fontSize: "1.6rem", color: "var(--fs-primary)" }}>
            FinanzasSys
          </div>
          <div className="text-muted small">Controla tus ingresos y gastos mensuales</div>
        </div>

        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <Form onSubmit={handleLogin}>
          <Form.Group className="mb-3">
            <Form.Label>Correo electrónico</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="tucorreo@ejemplo.com"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Contraseña</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </Form.Group>
          <div className="text-end mb-3">
            <Button variant="link" className="p-0 small" onClick={() => setMostrarRecuperar(true)}>
              ¿Olvidaste tu contraseña?
            </Button>
          </div>
          <Button type="submit" variant="primary" className="w-100" disabled={cargando}>
            {cargando ? <Spinner size="sm" animation="border" /> : "Ingresar"}
          </Button>
        </Form>

        <div className="text-center mt-3">
          <span className="text-muted small">¿No tienes cuenta? </span>
          <Button variant="link" className="p-0 small" onClick={() => setMostrarRegistro(true)}>
            Regístrate aquí
          </Button>
        </div>
      </div>

      <ModalRegistro show={mostrarRegistro} onHide={() => setMostrarRegistro(false)} />
      <ModalRecuperarPassword show={mostrarRecuperar} onHide={() => setMostrarRecuperar(false)} />
    </div>
  );
}

function ModalRegistro({ show, onHide }) {
  const vacio = { nombres: "", email: "", password: "", confirmarPassword: "", paisId: "", edad: "" };
  const [form, setForm] = useState(vacio);
  const [paises, setPaises] = useState([]);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [cargando, setCargando] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(null);

  useEffect(() => {
    if (show) paisesApi.listarPaises().then(setPaises);
    else { setForm(vacio); setError(""); setMensaje(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!mensaje) return;
    setSegundosRestantes(SEGUNDOS_REDIRECCION);
    const interval = setInterval(() => {
      setSegundosRestantes((s) => {
        if (s <= 1) {
          clearInterval(interval);
          onHide();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensaje]);

  const actualizar = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMensaje("");

    const errorPassword = validarPassword(form.password);
    if (errorPassword) { setError(errorPassword); return; }
    if (form.password !== form.confirmarPassword) { setError("Las contraseñas no coinciden."); return; }

    setCargando(true);
    try {
      const resultado = await authApi.registrarCliente({
        nombres: form.nombres,
        email: form.email,
        password: form.password,
        paisId: Number(form.paisId),
        edad: form.edad ? Number(form.edad) : null,
      });
      setMensaje(resultado.mensaje || "Registro exitoso. Revisa tu correo para validar tu cuenta.");
      setForm(vacio);
    } catch (err) {
      setError(err.response?.data?.mensaje || "No se pudo completar el registro.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-display" style={{ fontSize: "1.15rem" }}>Crear cuenta</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {mensaje && (
          <Alert variant="success" className="py-2">
            {mensaje} Volviendo al inicio de sesión en {segundosRestantes}s...
          </Alert>
        )}

        {!mensaje && (
          <Form onSubmit={handleSubmit} autoComplete="off">
            <Form.Group className="mb-3">
              <Form.Label>Nombres completos</Form.Label>
              <Form.Control value={form.nombres} onChange={actualizar("nombres")} required autoComplete="off" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control type="email" value={form.email} onChange={actualizar("email")} required autoComplete="off" />
            </Form.Group>
            <div className="row">
              <div className="col-6">
                <CampoPassword label="Contraseña" value={form.password} onChange={actualizar("password")} autoComplete="new-password" />
              </div>
              <div className="col-6">
                <CampoPassword label="Confirmar contraseña" value={form.confirmarPassword} onChange={actualizar("confirmarPassword")} autoComplete="new-password" />
              </div>
            </div>
            <Form.Text className="text-muted d-block mb-3" style={{ marginTop: "-0.75rem" }}>
              Mínimo 8 caracteres, incluyendo letras y números.
            </Form.Text>
            <div className="row">
              <div className="col-7">
                <Form.Group className="mb-3">
                  <Form.Label>País</Form.Label>
                  <Form.Select value={form.paisId} onChange={actualizar("paisId")} required autoComplete="off">
                    <option value="">Selecciona...</option>
                    {paises.map((p) => (
                      <option key={p.paisId} value={p.paisId}>{p.nombre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-5">
                <Form.Group className="mb-3">
                  <Form.Label>Edad <span className="text-muted small">(opcional)</span></Form.Label>
                  <Form.Control type="number" min={18} max={120} value={form.edad} onChange={actualizar("edad")} autoComplete="off" />
                </Form.Group>
              </div>
            </div>
            <Button type="submit" variant="primary" className="w-100" disabled={cargando}>
              {cargando ? <Spinner size="sm" animation="border" /> : "Registrarme"}
            </Button>
          </Form>
        )}

        <div className="text-center mt-3">
          <Button variant="link" className="p-0 small" onClick={onHide}>
            Volver a iniciar sesión
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function ModalRecuperarPassword({ show, onHide }) {
  const [email, setEmail] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!show) { setEmail(""); setMensaje(""); setError(""); }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      const resultado = await authApi.solicitarResetPassword(email);
      setMensaje(resultado.mensaje || "Si el correo está registrado, te enviamos un enlace para restablecer tu contraseña.");
    } catch (err) {
      setError(err.response?.data?.mensaje || "No se pudo procesar la solicitud.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-display" style={{ fontSize: "1.15rem" }}>Recuperar contraseña</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {mensaje ? (
          <>
            <Alert variant="success" className="py-2">{mensaje}</Alert>
            <Button variant="outline-secondary" className="w-100" onClick={onHide}>Volver a iniciar sesión</Button>
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <p className="text-muted small">Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>
            <Form.Group className="mb-3">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus placeholder="tucorreo@ejemplo.com" />
            </Form.Group>
            <Button type="submit" variant="primary" className="w-100" disabled={cargando}>
              {cargando ? <Spinner size="sm" animation="border" /> : "Enviar enlace"}
            </Button>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}
